// Firestore Collection Names
export const PROPERTIES_COLLECTION = 'properties';
export const TENANTS_COLLECTION = 'tenants';
export const LANDLORDS_COLLECTION = 'landlords';
export const USERS_COLLECTION = 'users';

// User Roles and Access Levels
export const USER_ROLES = {
  ADMINISTRATOR: 'administrator', // Full system access
  OWNER: 'owner',                // Property management company owner
  AGENT: 'agent',                // Property management agent
  INSPECTOR: 'inspector',        // Property inspector - conducts inspections
  LANDLORD: 'landlord'           // Property owner with limited access
};

// Role Levels (for access control)
export const ROLE_LEVELS = {
  [USER_ROLES.ADMINISTRATOR]: 0, // Full system access
  [USER_ROLES.OWNER]: 1,         // Owner access level
  [USER_ROLES.AGENT]: 2,         // Agent access level
  [USER_ROLES.INSPECTOR]: 2,     // Inspector access level (same as Agent)
  [USER_ROLES.LANDLORD]: 3       // Landlord access level (most restricted)
};

// Feature Access Levels - Define which roles can access which features
export const FEATURE_ACCESS = {
  // Dashboard features
  VIEW_DASHBOARD: 3,        // Dashboard access (Landlord+)
  
  // Property management features
  VIEW_PROPERTIES: 3,       // View properties (Landlord+)
  CREATE_PROPERTIES: 1,     // Create new properties (Owner+)
  EDIT_PROPERTIES: 1,       // Edit property details (Owner+)
  DELETE_PROPERTIES: 1,     // Delete properties (Owner+)
  VIEW_ALL_PROPERTIES: 2,   // View all properties (Agent+)
  
  // Financial features
  VIEW_FINANCE: 3,          // View finance (Landlord+)
  EDIT_FINANCE: 1,          // Edit finance records (Owner+)
  VIEW_PAYMENTS: 3,         // View payments (Landlord+)
  MANAGE_PAYMENTS: 1,       // Manage payments (Owner+)
  
  // Maintenance features
  VIEW_MAINTENANCE: 3,      // View maintenance (Landlord+)
  CREATE_MAINTENANCE: 3,    // Create maintenance requests (Landlord+)
  UPDATE_MAINTENANCE: 2,    // Update maintenance status (Agent+)
  MANAGE_MAINTENANCE: 1,    // Manage all maintenance requests (Owner+)
  
  // Tenant features
  VIEW_TENANTS: 2,          // View tenants page (Agent+)
  VIEW_ALL_TENANTS: 2,      // View all tenants (Agent+)
  CREATE_TENANTS: 2,        // Create new tenants (Agent+)
  EDIT_TENANTS: 2,          // Edit tenant details (Agent+)
  DELETE_TENANTS: 1,        // Delete tenants (Owner+)
  
  // Landlord features (clients)
  VIEW_LANDLORDS: 1,        // View landlords/clients (Owner+)
  VIEW_ALL_LANDLORDS: 1,    // View all landlords (Owner+)
  CREATE_LANDLORDS: 1,      // Create new landlords (Owner+)
  EDIT_LANDLORDS: 1,        // Edit landlord details (Owner+)
  DELETE_LANDLORDS: 1,      // Delete landlords (Owner+)
  
  // Communication features
  SEND_MESSAGES: 3,         // Send messages (Landlord+)
  VIEW_MESSAGES: 3,         // View messages (Landlord+)
  VIEW_NOTIFICATIONS: 3,    // View notifications (Landlord+)
  SEND_BROADCASTS: 2,       // Send broadcasts (Agent+)
  
  // Link management features
  VIEW_LINKS: 3,            // View quick links (Landlord+)
  MANAGE_LINKS: 2,          // Manage quick links (Agent+)
  
  // Document features
  VIEW_DOCUMENTS: 3,        // View documents (Landlord+)
  UPLOAD_DOCUMENTS: 2,      // Upload documents (Agent+)
  DELETE_DOCUMENTS: 1,      // Delete documents (Owner+)
  VIEW_CERTIFICATES: 2,     // View certificates (Agent+)
  MANAGE_CERTIFICATES: 1,   // Manage certificates (Owner+)
  
  // Calendar features (all roles that use app need view + manage for bookings)
  VIEW_CALENDAR: 3,         // View calendar (Landlord+)
  MANAGE_CALENDAR: 3,       // Manage calendar / book inspections (Landlord+)
  
  // Inspection features (booking and management)
  VIEW_INSPECTIONS: 3,      // View inspections page (Landlord+)
  MANAGE_INSPECTIONS: 3,    // Book and manage inspections (Landlord+)
  
  // Reporting features
  VIEW_REPORTS: 3,          // View reports (Landlord+)
  EXPORT_DATA: 1,           // Export data reports (Owner+)
  
  // Archive features
  VIEW_ARCHIVE: 1,          // View archived items (Owner+)
  MANAGE_ARCHIVE: 1,        // Manage archived items (Owner+)
  
  // User management features
  VIEW_USERS: 1,            // View user list (Owner+)
  EDIT_USERS: 1,            // Edit user accounts (Owner+)
  CREATE_USERS: 1,          // Create user accounts (Owner+)
  MANAGE_AGENTS: 1,         // Manage agent accounts (Owner+)
  DELETE_USERS: 0,          // Delete user accounts (Admin only)
  
  // System features
  ACCESS_SETTINGS: 1,       // Access system settings (Owner+)
  MANAGE_ROLES: 0,          // Manage user roles (Admin only)
  SYSTEM_CONFIG: 0,         // Configure system settings (Admin only)
  VIEW_AUDIT_LOGS: 1,       // View audit logs (Owner+)
  MANAGE_BRANDING: 1,       // Manage company branding (Owner+)
  IMPORT_EXPORT_DATA: 1     // Import/export data (Owner+)
};

// Page Access Levels - Define which roles can access which pages
export const PAGE_ACCESS = {
  // Core pages
  DASHBOARD: 3,             // Dashboard view (Landlord+)
  PROPERTIES: 3,            // Properties management (Landlord+)
  MAINTENANCE: 3,           // Maintenance requests (Landlord+)
  FINANCE: 3,               // Finance records (Landlord+)
  MESSAGES: 3,              // Messaging system (Landlord+)
  LINKS: 3,                 // Quick links (Landlord+)
  BROADCAST: 2,             // Broadcast system (Agent+)
  REPORTS: 3,               // Reports view (Landlord+)
  TENANTS: 2,               // Tenant management (Agent+)
  CONTRACTORS: 2,           // Contractor management (Agent+)
  LANDLORDS: 1,             // Landlord/client management (Owner+)
  CALENDAR: 3,              // Calendar view (Landlord+)
  INSPECTIONS: 3,           // Inspections & booking (Landlord+)
  DOCUMENTS: 3,             // Document management (Landlord+)
  
  // Additional functional pages
  NOTIFICATIONS: 3,         // Notifications page (Landlord+)
  PAYMENTS: 3,              // Payments page (Landlord+)
  CERTIFICATES: 2,          // Certificate management (Agent+)
  PROFILE: 3,               // User profile (Landlord+)
  ACCOUNT: 3,               // Account management (Landlord+)
  
  // Archive pages
  ARCHIVE: 1,               // Archive view (Owner+)
  ARCHIVED_PROPERTIES: 1,   // Archived properties (Owner+)
  ARCHIVED_TENANTS: 1,      // Archived tenants (Owner+)
  
  // Administrative pages
  SETTINGS: 1,              // System settings (Owner+)
  USERS: 1,                 // User management (Owner+)
  AUDIT: 1,                 // Audit logs (Owner+)
  
  // System configuration pages (Admin only)
  ROLES: 0,                 // Role management (Admin only)
  SYSTEM: 0,                // System configuration (Admin only)
  PERMISSIONS: 0            // Permission management (Admin only)
};

// Property Statuses
export const PROPERTY_STATUSES = {
  VACANT: 'vacant',
  OCCUPIED: 'occupied',
  UNDER_MAINTENANCE: 'under_maintenance'
};

// Certificate Types
export const CERTIFICATE_TYPES = {
  GAS_SAFETY: 'GAS_SAFETY',
  ELECTRICAL_SAFETY: 'ELECTRICAL_SAFETY',
  ENERGY_PERFORMANCE: 'ENERGY_PERFORMANCE',
  HMO_LICENSE: 'HMO_LICENSE'
};
